import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Alert, Button } from "react-bootstrap";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useChat } from "@/contexts/ChatContext";
import {
  useGetChatConversationQuery,
  useMarkChatReadMutation,
} from "@/services/chat";
import { compareChatRevision } from "@/hooks/useChatSync";
import { useChatScroll } from "@/hooks/useChatScroll";
import ConversationList from "@/components/molecules/chat/ConversationList";
import ConversationHeader from "@/components/molecules/chat/ConversationHeader";
import MessageList from "@/components/molecules/chat/MessageList";
import CreateConversationModal from "@/components/molecules/chat/CreateConversationModal";
import MessageComposer from "@/components/molecules/chat/MessageComposer";
import { plainText } from "@/helpers/chatText";
import GroupMembersModal from "@/components/molecules/chat/GroupMembersModal";

export default function ChatPage() {
  const { conversationId } = useParams();
  const [search] = useSearchParams();
  const navigate = useNavigate();
  const target = search.get("message");
  const chat = useChat();
  const {
    actorId,
    activeConversationId,
    setActiveConversationId,
    loadMessages,
    messages,
    before,
    after,
  } = chat;
  const selection = useRef(setActiveConversationId);
  selection.current = setActiveConversationId;
  const [create, setCreate] = useState(false),
    [members, setMembers] = useState(false),
    [loading, setLoading] = useState(false),
    [loadError, setLoadError] = useState(false),
    [lost, setLost] = useState(false);
  const selectedPreviously = useRef<string | null>(null);
  useEffect(() => {
    setLost(false);
    setMembers(false);
    selectedPreviously.current = null;
    selection.current(conversationId || null);
  }, [conversationId, actorId]);
  useEffect(() => () => selection.current(null), []);
  useEffect(() => {
    if (activeConversationId === conversationId)
      selectedPreviously.current = activeConversationId;
    if (
      conversationId &&
      selectedPreviously.current === conversationId &&
      !activeConversationId
    ) {
      setLost(true);
      setMembers(false);
    }
  }, [activeConversationId, conversationId]);
  const selected = !!conversationId && activeConversationId === conversationId;
  const detail = useGetChatConversationQuery(
    { actorId: actorId!, conversationId: conversationId! },
    { skip: !actorId || !selected },
  );
  const snapshot =
    chat.conversation?.id === conversationId ? chat.conversation : null;
  const conversation = selected
    ? snapshot &&
      (!detail.currentData ||
        compareChatRevision(snapshot.revision, detail.currentData.revision) >=
          0)
      ? snapshot
      : detail.currentData
    : null;
  const [markRead] = useMarkChatReadMutation();
  // Each selection owns its acknowledgement state. An authorized mutation may
  // finish after navigation, but its completion must never advance another view.
  const readScope = useMemo(
    () => ({
      lastRead: "0",
      pending: null as { id: string; sequence: string } | null,
      timer: null as ReturnType<typeof setTimeout> | null,
      active: true,
    }),
    [actorId, conversationId, activeConversationId],
  );
  useEffect(() => {
    readScope.active = true;
    return () => {
      readScope.active = false;
      if (readScope.timer) clearTimeout(readScope.timer);
    };
  }, [readScope]);
  const onVisibleMessage = useCallback(
    (id: string) => {
      const message = messages.find((m) => m.id === id);
      if (
        !readScope.active ||
        !conversation?.isMember ||
        !actorId ||
        !message ||
        compareChatRevision(message.sequence, readScope.lastRead) <= 0
      )
        return;
      if (
        !readScope.pending ||
        compareChatRevision(message.sequence, readScope.pending.sequence) > 0
      )
        readScope.pending = { id, sequence: message.sequence };
      if (readScope.timer) clearTimeout(readScope.timer);
      readScope.timer = setTimeout(() => {
        const pending = readScope.pending;
        readScope.pending = null;
        if (!readScope.active || !pending || document.hidden) return;
        markRead({
          actorId,
          conversationId: conversation.id,
          throughMessageId: pending.id,
        })
          .unwrap()
          .then(() => {
            if (
              readScope.active &&
              compareChatRevision(pending.sequence, readScope.lastRead) > 0
            )
              readScope.lastRead = pending.sequence;
          })
          .catch(() => {});
      }, 250);
    },
    [messages, conversation, actorId, markRead, readScope],
  );
  const older = useCallback(async () => {
    if (!before || loading) return;
    setLoading(true);
    const page = await loadMessages({ before });
    setLoadError(!page);
    setLoading(false);
  }, [before, loading, loadMessages]);
  const scroll = useChatScroll({
    conversationId: conversation?.id ?? null,
    messages,
    onOlder: older,
    onVisibleMessage,
  });
  const { scrollToMessage, scrollToLatest } = scroll;
  useEffect(() => {
    if (!selected) return;
    let live = true;
    setLoading(true);
    setLoadError(false);
    if (target) scrollToMessage(target);
    loadMessages(target ? { around: target } : undefined).then((page) => {
      if (live) {
        setLoading(false);
        setLoadError(!page);
      }
    });
    return () => {
      live = false;
    };
  }, [selected, conversationId, target, loadMessages, scrollToMessage]);
  const newer = async (boundary = after) => {
    if (!boundary || loading) return;
    setLoading(true);
    const page = await loadMessages({ after: boundary });
    setLoading(false);
    setLoadError(!page);
  };
  const jump = async (id: string) => {
    if (!scrollToMessage(id)) {
      setLoading(true);
      const page = await loadMessages({ around: id });
      setLoading(false);
      setLoadError(!page);
    }
  };
  return (
    <main
      className={`chat-workspace ${conversationId ? "has-selection" : ""}`}
      aria-label="Messagerie interne"
    >
      <ConversationList
        selectedId={conversationId}
        onCreate={() => setCreate(true)}
      />
      <section className="chat-panel" aria-label="Conversation">
        {chat.connectionState === "blocked" ? (
          <div className="chat-empty" role="alert">
            La messagerie n’est pas accessible dans cette session.
            Reconnectez-vous avec votre propre compte.
          </div>
        ) : lost ? (
          <div className="chat-empty">
            <Alert variant="warning">
              Vous n’avez plus accès à cette conversation.
            </Alert>
            <Button onClick={() => navigate("/chat")}>
              Retour aux conversations
            </Button>
          </div>
        ) : conversation ? (
          <>
            <ConversationHeader
              conversation={conversation}
              onMembers={() => setMembers(true)}
            />
            {chat.connectionState === "disconnected" && (
              <div className="chat-notice" role="status">
                Connexion interrompue. Reconnexion automatique…
              </div>
            )}
            {loadError && (
              <div className="chat-notice" role="alert">
                Impossible de charger les messages.{" "}
                <Button
                  variant="link"
                  onClick={() => loadMessages().then((p) => setLoadError(!p))}
                >
                  Réessayer
                </Button>
              </div>
            )}
            <MessageList
              messages={messages}
              gaps={chat.historyGaps}
              onGap={newer}
              historyRef={scroll.historyRef}
              onScroll={scroll.onScroll}
              onOlder={older}
              hasOlder={!!before}
              hasAfter={!!after}
              onAfter={() => newer()}
              loading={loading}
              onJump={jump}
              canWrite={conversation.canWrite}
              onReply={(message) => {
                chat.setDraft(conversation.id, {
                  parts: chat.drafts[conversation.id]?.parts || [],
                  replyToId: message.id,
                  reply: {
                    id: message.id,
                    authorName: message.author.name,
                    excerpt: Array.from(plainText(message.parts))
                      .slice(0, 240)
                      .join(""),
                  },
                });
                requestAnimationFrame(
                  () =>
                    document
                      .querySelector<HTMLTextAreaElement>(
                        ".chat-composer textarea",
                      )
                      ?.focus(),
                );
              }}
            />
            {scroll.hasNewMessages && (
              <Button
                className="chat-new-messages"
                size="sm"
                onClick={scrollToLatest}
              >
                Nouveaux messages ↓
              </Button>
            )}
            <MessageComposer
              key={`${actorId}:${conversation.id}`}
              conversationId={conversation.id}
              canWrite={conversation.canWrite}
              onSent={scrollToLatest}
            />
          </>
        ) : (
          <div className="chat-empty">
            {conversationId
              ? "Chargement de la conversation…"
              : "Sélectionnez une conversation ou commencez un nouvel échange."}
            {!conversationId && (
              <Button variant="outline-primary" onClick={() => setCreate(true)}>
                Commencer une conversation
              </Button>
            )}
          </div>
        )}
      </section>
      {create && (
        <CreateConversationModal
          onClose={() => setCreate(false)}
          onCreated={(c) => {
            setCreate(false);
            navigate(`/chat/${c.id}`);
          }}
        />
      )}
      {members && conversation && (
        <GroupMembersModal
          conversation={conversation}
          onClose={() => setMembers(false)}
        />
      )}
    </main>
  );
}
